
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { CreateSupermarketPriceInput, SupermarketPrice, SupermarketPriceFilters } from '@/types/database/supermarket-prices';
import { useAuth } from '@/contexts/AuthContext';

export const useSupermarketPrices = (filters: SupermarketPriceFilters = {}) => {
  return useQuery({
    queryKey: ['supermarket-prices', filters],
    queryFn: async () => {
      let query = supabase
        .from('supermarket_prices')
        .select('*')
        .order('date', { ascending: false });

      if (filters.product_name) {
        query = query.ilike('product_name', `%${filters.product_name}%`);
      }

      if (filters.location) {
        query = query.ilike('location', `%${filters.location}%`);
      }

      if (filters.date_from) {
        query = query.gte('date', filters.date_from);
      }

      if (filters.date_to) {
        query = query.lte('date', filters.date_to);
      }

      const { data, error } = await query;

      if (error) {
        throw error;
      }

      return data as SupermarketPrice[];
    },
  });
};

export const useCreateSupermarketPrice = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (price: CreateSupermarketPriceInput) => {
      const { data, error } = await supabase
        .from('supermarket_prices')
        .insert([{ ...price, created_by: user?.id }])
        .select()
        .single();

      if (error) {
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supermarket-prices'] });
    },
  });
};

export const useUpdateSupermarketPrice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, price }: { id: string; price: CreateSupermarketPriceInput }) => {
      const { data, error } = await supabase
        .from('supermarket_prices')
        .update(price)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supermarket-prices'] });
    },
  });
};

export const useDeleteSupermarketPrice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('supermarket_prices')
        .delete()
        .eq('id', id);

      if (error) {
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supermarket-prices'] });
    },
  });
};

export const useBulkCreateSupermarketPrices = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (prices: CreateSupermarketPriceInput[]) => {
      const pricesWithUser = prices.map(price => ({
        ...price,
        created_by: user?.id,
      }));

      const { data, error } = await supabase
        .from('supermarket_prices')
        .insert(pricesWithUser)
        .select();

      if (error) {
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supermarket-prices'] });
    },
  });
};
